import React, { useEffect, useState } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopBar from '../../components/AdminTopBar';
import { API_BASE_URL } from '../../config';
import PanelHelpBadge from '../../components/admin/PanelHelpBadge';
import DashboardModuleTabs, { DASHBOARD_WORKSPACE_HIDDEN_PATHS } from '../../components/admin/dashboard/DashboardModuleTabs';

const getAdminHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const INSIGHT_CODE_MAP = {
  LOW_PLAN_ADHERENCE: {
    title: 'Plan Uyumu Düşük',
    description: 'Kullanıcı planladığı yemekleri beklenen oranda uygulayamıyor.'
  },
  HIGH_DECISION_TIME: {
    title: 'Karar Süresi Uzun',
    description: 'Kullanıcı seçim yapmakta zorlandığı için karar süresi uzuyor.'
  },
  LOW_PRIMARY_ACCEPT: {
    title: 'Birincil Öneri Kabulü Düşük',
    description: 'Sistemin birinci önerisi beklenen oranda seçilmiyor.'
  },
  LOW_FALLBACK_HEALTH: {
    title: 'Alternatif Öneri Sağlığı Düşük',
    description: 'İkinci ve üçüncü seçeneklerin performansı zayıf kalıyor.'
  }
};

const ACTION_KEY_MAP = {
  maxRecipesShown: {
    title: 'Gösterilen Tarif Sayısı',
    description: 'Ekranda aynı anda gösterilen tarif üst sınırını belirler.'
  },
  simplifyPlan: {
    title: 'Planı Sadeleştir',
    description: 'Kullanıcıya daha sade ve uygulaması kolay karar seti sunar.'
  },
  quickMealsBoost: {
    title: 'Hızlı Tarif Güçlendirmesi',
    description: 'Hazırlama süresi kısa tariflerin ağırlığını artırır.'
  },
  improvePrimaryReasoning: {
    title: 'Birincil Öneri Gerekçe İyileştirme',
    description: 'Birinci önerinin açıklama kalitesini güçlendirir.'
  },
  widenSecondaryDiversity: {
    title: 'Alternatif Çeşitliliği Artır',
    description: 'İkinci ve üçüncü öneride tek tip tekrarını azaltır.'
  },
  reassurance_blocked_cooldown: {
    title: 'Destek Mesajı Sıklık Engeli',
    description: 'Aynı destek mesajının çok sık gösterilmesini engeller.'
  }
};

function getInsightInfo(code) {
  return INSIGHT_CODE_MAP[code] || {
    title: code || 'Bilinmeyen İçgörü',
    description: 'Bu kod için açıklama henüz tanımlı değil.'
  };
}

function getActionInfo(actionKey) {
  return ACTION_KEY_MAP[actionKey] || {
    title: actionKey || 'Bilinmeyen Aksiyon',
    description: 'Bu aksiyon anahtarı için açıklama henüz tanımlı değil.'
  };
}

export default function BehaviorInsightsPage() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [insights, setInsights] = useState([]);
  const [blast, setBlast] = useState({ actions: [], affectedUsers: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const headers = getAdminHeaders();
        const [insightRes, blastRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/admin/kitchen/behavior/insights`, { credentials: 'include', headers }),
          fetch(`${API_BASE_URL}/api/admin/kitchen/behavior/blast-radius?days=7`, { credentials: 'include', headers })
        ]);
        const insightJson = insightRes.ok ? await insightRes.json() : null;
        const blastJson = blastRes.ok ? await blastRes.json() : null;

        if (!mounted) return;
        if (!insightRes.ok) throw new Error(insightJson?.error || 'insights_fetch_failed');
        if (!blastRes.ok) throw new Error(blastJson?.error || 'blast_radius_fetch_failed');

        setInsights(Array.isArray(insightJson?.insights) ? insightJson.insights : []);
        setBlast(blastJson?.blastRadius || { actions: [], affectedUsers: 0 });
      } catch (err) {
        if (mounted) setError(err?.message || 'İçgörü verileri alınamadı');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-[#0d141c]">
      <AdminSidebar
        mobileOpen={mobileSidebarOpen}
        onCloseMobile={() => setMobileSidebarOpen(false)}
        hiddenPaths={DASHBOARD_WORKSPACE_HIDDEN_PATHS}
      />
      <main className="flex-1 md:ml-[var(--admin-sidebar-width,18rem)] flex flex-col min-h-screen transition-[margin] duration-300">
        <AdminTopBar title="Davranış İçgörüleri" onToggleMobile={() => setMobileSidebarOpen(true)} />

        <div className="p-4 sm:p-8 flex-1 space-y-6">
          <DashboardModuleTabs />
          <header className="space-y-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-black text-slate-800 dark:text-white">İçgörü - Aksiyon Matrisi</h1>
              <PanelHelpBadge
                title="İçgörü - Aksiyon Matrisi"
                description="Kuralların hangi içgörüyü tetiklediğini ve bunun hangi aksiyona dönüştüğünü gösterir."
                interpretation="İçgörü sorunu tarif eder, aksiyon ise motorun neyi değiştirdiğini gösterir."
                metricExample="Aynı aksiyon kodu tekrar artıyorsa ilgili kural daha sık tetikleniyordur."
              />
            </div>
            <p className="text-sm text-slate-500">Kısa özet: Bu sayfa karar motorunun neden-sonuç akışını okunur şekilde açıklar.</p>
            {error ? <p className="text-sm font-bold text-rose-600">{error}</p> : null}
          </header>

          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <p className="text-xs uppercase text-slate-500">İçgörü Sayısı</p>
              <p className="text-2xl font-black text-slate-900 dark:text-white">{insights.length}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <p className="text-xs uppercase text-slate-500">Etkilenen Kullanıcı (7g)</p>
              <p className="text-2xl font-black text-slate-900 dark:text-white">{Number(blast?.affectedUsers || 0)}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <p className="text-xs uppercase text-slate-500">Aksiyon Türü (7g)</p>
              <p className="text-2xl font-black text-slate-900 dark:text-white">{Array.isArray(blast?.actions) ? blast.actions.length : 0}</p>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">İçgörü Tanımları</h2>
              <PanelHelpBadge
                title="İçgörü Tanımları"
                description="Her satır bir kural kodunu, önceliğini ve bağlı aksiyonu gösterir."
                interpretation="Kod adının altındaki açıklama, bu kodun gerçekte neyi anlattığını sade dille verir."
                metricExample="Öncelik numarası küçük olan kodlar operasyon sırasında önce ele alınır."
              />
            </div>
            {loading ? <p className="text-sm text-slate-500">Yükleniyor...</p> : null}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="py-2 pr-3">Kod</th>
                    <th className="py-2 pr-3">Öncelik</th>
                    <th className="py-2 pr-3">Durum</th>
                    <th className="py-2 pr-3">Aksiyon</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.map((row) => {
                    const info = getInsightInfo(row.code);
                    const actionInfo = getActionInfo(row.action_json?.actionKey);
                    return (
                      <tr key={row.id} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="py-2 pr-3">
                          <div className="space-y-1">
                            <p className="font-semibold text-slate-800 dark:text-slate-100">{info.title}</p>
                            <p className="text-xs text-slate-500">{info.description}</p>
                            <p className="text-[11px] text-slate-500">Teknik kod: {row.code}</p>
                          </div>
                        </td>
                        <td className="py-2 pr-3">{row.priority}</td>
                        <td className="py-2 pr-3">{row.enabled ? 'Açık' : 'Kapalı'}</td>
                        <td className="py-2 pr-3">
                          <p className="font-semibold text-slate-800 dark:text-slate-100">{actionInfo.title}</p>
                          <p className="text-xs text-slate-500">{actionInfo.description}</p>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Etki Alanı</h2>
              <PanelHelpBadge
                title="Etki Alanı"
                description="Son 7 günde çalışan aksiyon anahtarlarını olay ve kullanıcı adedi ile listeler."
                interpretation="Aksiyon anahtarı ne kadar çok görünürse motor o davranışa o kadar sık müdahale etmiş demektir."
                metricExample="Aksiyon olayı artarken kullanıcı sayısı düşükse aynı kullanıcılarda tekrar olabilir."
              />
            </div>
            <div className="space-y-2">
              {(blast?.actions || []).map((row, idx) => {
                const info = getActionInfo(row.action_key);
                return (
                  <div key={`${row.action_key}-${idx}`} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 p-3 text-sm">
                    <div className="space-y-1">
                      <span className="font-bold text-slate-800 dark:text-slate-100">{info.title}</span>
                      <p className="text-xs text-slate-500">{info.description}</p>
                      <p className="text-[11px] text-slate-500">Teknik anahtar: {row.action_key}</p>
                    </div>
                    <span className="text-slate-500">Olay: {row.action_count} · Kullanıcı: {row.user_count}</span>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
